// AI聊天服務
import { detectLanguage, getLanguageConfig, SupportedLanguage, languageHistory } from '../utils/languageDetection';

export interface ChatMessage {
  id: string;
  content: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  isTyping?: boolean;
  language?: SupportedLanguage; // 添加語言信息
}

export interface Policy {
  id: string;
  name: string;
  company?: string;
  annualPremium?: number;
  category?: string;
  coverage?: string;
  deductible?: string;
  copay?: string;
  benefitAmount?: string;
  waitingPeriod?: string;
  petName?: string;
  annualLimit?: string;
  liabilityLimit?: string;
  annualMaximum?: string;
  preventiveCoverage?: string;
  basicCoverage?: string;
  majorCoverage?: string;
  hospitalBenefit?: string;
  weeklyBenefit?: string;
  duration?: string;
}

// 問題類型分類
export type QuestionType = 'POLICY_SPECIFIC' | 'INSURANCE_GENERAL' | 'SUREPAL_QUESTION' | 'NON_INSURANCE';

// 問題分類器
export const classifyQuestion = (question: string): QuestionType => {
  const lowerQuestion = question.toLowerCase();
  
  // 保單特定問題關鍵字
  const policyKeywords = [
    '我的保單', '這個保單', '保單內容', '我的保險', '這個保險',
    '我的policy', 'this policy', 'my policy', '保單理賠', '保單保障',
    'my insurance', 'my policies', 'my premium', 'my coverage',
    'how much do i pay', 'what do i pay', 'my annual premium',
    'my monthly premium', 'my deductible', 'my copay',
    '保費', '理賠', '保障', '自負額', '共付額'
  ];
  
  // SurePal相關問題關鍵字
  const surepalKeywords = [
    'surepal', 'sure pal', 'surepal是什麼', '什麼是surepal', 'surepal功能',
    'surepal能做什麼', 'surepal服務', 'surepal平台', 'surepal公司',
    'surepal目標', 'surepal願景', 'surepal使命', 'surepal特色',
    'surepal優勢', 'surepal未來', 'surepal發展', 'surepal計劃',
    'surepal團隊', 'surepal創辦人', 'surepal成立', 'surepal歷史'
  ];
  
  // 保險相關問題關鍵字
  const insuranceKeywords = [
    '保險', '保單', '理賠', '車禍', '手術', '醫療', '意外', '疾病',
    'insurance', 'claim', 'accident', 'surgery', 'medical', 'health',
    'coverage', 'premium', 'deductible', 'copay', 'benefit'
  ];
  
  // 檢查是否為保單特定問題
  if (policyKeywords.some(keyword => lowerQuestion.includes(keyword))) {
    return 'POLICY_SPECIFIC';
  }
  
  // 檢查是否為SurePal相關問題
  if (surepalKeywords.some(keyword => lowerQuestion.includes(keyword))) {
    return 'SUREPAL_QUESTION';
  }
  
  // 檢查是否為保險相關問題
  if (insuranceKeywords.some(keyword => lowerQuestion.includes(keyword))) {
    return 'INSURANCE_GENERAL';
  }
  
  // 其他問題
  return 'NON_INSURANCE';
};

// 格式化保單數據
export const formatPolicyData = (policies: Policy[]): string => {
  if (policies.length === 0) {
    return 'No policy information available.';
  }
  
  return policies.map(policy => {
    let policyInfo = `Policy Name: ${policy.name}\n`;
    policyInfo += `Company: ${policy.company || 'Unknown'}\n`;
    // 明確標註年繳保費（不是總保費）
    policyInfo += `Annual Premium (年繳保費): $${policy.annualPremium || 0}\n`;
    policyInfo += `Note: This is the yearly payment amount, not the total lifetime cost.\n`;
    policyInfo += `Category: ${policy.category || 'Unknown'}\n`;
    
    if (policy.coverage) {
      policyInfo += `Coverage: ${policy.coverage}\n`;
    }
    if (policy.deductible) {
      policyInfo += `Deductible: ${policy.deductible}\n`;
    }
    if (policy.copay) {
      policyInfo += `Copay: ${policy.copay}\n`;
    }
    if (policy.benefitAmount) {
      policyInfo += `Benefit Amount: ${policy.benefitAmount}\n`;
    }
    if (policy.annualLimit) {
      policyInfo += `Annual Limit: ${policy.annualLimit}\n`;
    }
    if (policy.liabilityLimit) {
      policyInfo += `Liability Limit: ${policy.liabilityLimit}\n`;
    }
    
    return policyInfo;
  }).join('\n---\n');
};

// 生成AI回應
export const generateAIResponse = async (
  question: string, 
  policies: Policy[], 
  questionType: QuestionType,
  userId?: string
): Promise<string> => {
  
  // 檢測用戶輸入的語言
  const detectedLanguage = detectLanguage(question);
  const languageConfig = getLanguageConfig(detectedLanguage);
  
  // 記錄用戶語言偏好
  if (userId) {
    languageHistory.recordUserLanguage(userId, detectedLanguage);
  }
  
  console.log('🌐 語言檢測結果:', {
    detectedLanguage,
    languageName: languageConfig.nativeName,
    question: question.substring(0, 50) + '...'
  });
  
  // SurePal相關問題直接回答
  if (questionType === 'SUREPAL_QUESTION') {
    if (detectedLanguage === 'zh' || detectedLanguage === 'zh-TW') {
      return `SurePal 是一個創新的保險管理平台，旨在簡化並提升您的保險體驗。以下是 SurePal 的目標：

🎯 **我們的使命**：讓保險管理變得簡單、透明且人人可及。

🚀 **目前 MVP 功能**：
- AI Pal 可以根據您現有的保單回答問題
- 保單概覽和摘要管理
- 不同保險類型的智能分類
- 即時保費計算和分析

🔮 **即將推出的完整版本**：
- 完整的保單文件上傳和分析
- 詳細的保障說明和理賠指導
- 個人化保險建議
- 先進的 AI 保單條款和條件分析
- 全面的保險組合管理

💡 **我們的願景**：我們相信每個人都應該能夠輕鬆了解自己的保險保障，並對自己的保護需求做出明智的決定。

目前您正在體驗我們的 MVP 版本，我們可以提供一般保險建議並分析您現有的保單摘要。完整版本將允許您上傳完整的保單文件，以獲得更詳細和個人化的協助。

您想了解更多關於 SurePal 的任何特定方面嗎？`;
    } else if (detectedLanguage === 'zh-CN') {
      return `SurePal 是一个创新的保险管理平台，旨在简化并提升您的保险体验。以下是 SurePal 的目标：

🎯 **我们的使命**：让保险管理变得简单、透明且人人可及。

🚀 **目前 MVP 功能**：
- AI Pal 可以根据您现有的保单回答问题
- 保单概览和摘要管理
- 不同保险类型的智能分类
- 即时保费计算和分析

🔮 **即将推出的完整版本**：
- 完整的保单文件上传和分析
- 详细的保障说明和理赔指导
- 个性化保险建议
- 先进的 AI 保单条款和条件分析
- 全面的保险组合管理

💡 **我们的愿景**：我们相信每个人都应该能够轻松了解自己的保险保障，并对自己的保护需求做出明智的决定。

目前您正在体验我们的 MVP 版本，我们可以提供一般保险建议并分析您现有的保单摘要。完整版本将允许您上传完整的保单文件，以获得更详细和个性化的协助。

您想了解更多关于 SurePal 的任何特定方面吗？`;
    } else {
      // 默認英文
      return `SurePal is an innovative insurance management platform designed to simplify and enhance your insurance experience. Here's what SurePal aims to achieve:

🎯 **Our Mission**: To make insurance management simple, transparent, and accessible for everyone.

🚀 **Current MVP Features**:
- AI Pal that can answer questions based on your existing policies
- Policy overview and summary management
- Smart categorization of different insurance types
- Real-time premium calculations and analysis

🔮 **Upcoming Full Version**:
- Complete policy document upload and analysis
- Detailed coverage explanations and claim guidance
- Personalized insurance recommendations
- Advanced AI analysis of policy terms and conditions
- Comprehensive insurance portfolio management

💡 **Our Vision**: We believe that everyone should have easy access to understand their insurance coverage and make informed decisions about their protection needs.

Currently, you're experiencing our MVP version where we can provide general insurance advice and analyze your existing policy summaries. The full version will allow you to upload complete policy documents for more detailed and personalized assistance.

Would you like to know more about any specific aspect of SurePal?`;
    }
  }
  
  // 非保險問題返回罐頭訊息
  if (questionType === 'NON_INSURANCE') {
    if (detectedLanguage === 'zh' || detectedLanguage === 'zh-TW') {
      return '我們只提供與保險相關的回應。請詢問保險相關的問題。';
    } else if (detectedLanguage === 'zh-CN') {
      return '我们只提供与保险相关的回应。请询问保险相关的问题。';
    } else {
      return '我們只提供與保險相關的回應。請詢問保險相關問題。';
    }
  }
  
  // 檢查是否有OpenAI API Key（從環境變數讀取，不寫死在程式碼裡）
  const apiKey = process.env.REACT_APP_OPENAI_API_KEY;

  if (!apiKey) {
    console.error('❌ 找不到 REACT_APP_OPENAI_API_KEY 環境變數');
    return 'AI服務暫時無法使用，請稍後再試。請設置OpenAI API Key。';
  }
  
  try {
    let prompt = '';
    
    if (questionType === 'POLICY_SPECIFIC') {
      const policyData = formatPolicyData(policies);

      // 注意：這裡故意不再對「手術/治療/藥物」等關鍵字做罐頭拒答。
      // 這條服務是 RAG（chatWithPolicy）失敗時的備用路線，手上只有保單摘要欄位、
      // 沒有完整 PDF 內容，但仍應該盡力用現有資料回答，而不是直接放棄；
      // 如果摘要欄位真的沒有答案，交給下面的 prompt 指示 AI 誠實說明查無資料即可。
      prompt = `You are a professional insurance advisor. The user is asking about their specific insurance policies. Please answer their question using ONLY the policy information provided below.

IMPORTANT: Use the exact numbers and details from the policy information. Do not give generic advice - give specific answers based on their actual policies.

Policy Information:
${policyData}

User Question: ${question}

Instructions:
1. When asked about premiums:
   - If asked about "total premium" or "總保費", sum all annual premiums
   - If asked about "annual premium" or "年繳保費", show each policy's annual premium separately
   - IMPORTANT: "Annual Premium" means the yearly payment amount, NOT the total lifetime cost
   - Always clarify: "年繳保費" = yearly payment, "總保費" = sum of all policies' annual premiums
2. Provide specific coverage details from the policy information
3. Use exact numbers from the policies
4. If asked about "my" insurance, refer to the specific policies listed above
5. Be specific and direct in your answers
6. The policy information above only contains summary fields, not the full policy document text.
   If the user asks about something not covered by these fields (e.g. specific surgeries, exact
   claim conditions, fine-print exclusions), say plainly that you don't have that level of detail
   in the summary, and suggest they double-check with the full policy document — do NOT refuse to
   answer just because the question sounds "detailed".

Please provide a direct answer based on the policy information provided.`;
    } else if (questionType === 'INSURANCE_GENERAL') {
      prompt = `You are a professional insurance advisor for SurePal. Please answer the following insurance-related question:

User Question: ${question}

Please provide professional, accurate, and easy-to-understand insurance advice. If the question involves specific policy terms or claim conditions, please explain the general situation. 

IMPORTANT: Do not direct users to contact their insurance provider or review policy documents. Instead, mention that SurePal's upcoming full version will allow users to upload their complete policy documents for more detailed analysis.`;
    }
    
    // 調用OpenAI API
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`
      },
      body: JSON.stringify({
        model: 'gpt-3.5-turbo',
        messages: [
          {
            role: 'system',
            content: languageConfig.systemPrompt
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        max_tokens: 500,
        temperature: 0.7
      })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ OpenAI API錯誤詳情:', {
        status: response.status,
        statusText: response.statusText,
        error: errorText
      });
      throw new Error(`OpenAI API error: ${response.status} - ${errorText}`);
    }
    
    const data = await response.json();
    return data.choices[0].message.content.trim();
    
  } catch (error) {
    console.error('AI回應生成失敗:', error);
    return '抱歉，AI服務暫時不可用。請稍後再試。';
  }
};

// 主要的AI聊天服務
export class AIChatService {
  async processMessage(question: string, policies: Policy[], userId?: string): Promise<string> {
    let questionType = classifyQuestion(question);

    // classifyQuestion 只能靠關鍵字比對，問法稍微換一下就會漏判成「一般保險問題」，
    // 完全不會帶入用戶的保單資料。只要用戶手上有保單，且問題本來就跟保險相關
    // （不是被歸類成 SurePal 問題或非保險問題），就優先當作保單特定問題處理，
    // 讓 AI 有機會用實際保單資料回答，而不是給空泛的通用建議。
    if (questionType === 'INSURANCE_GENERAL' && policies.length > 0) {
      questionType = 'POLICY_SPECIFIC';
    }

    console.log('🔍 問題分類結果:', {
      question: question,
      questionType: questionType,
      policiesCount: policies.length
    });
    return await generateAIResponse(question, policies, questionType, userId);
  }
}

export const aiChatService = new AIChatService();
